import React, { FormEvent, useState, useRef } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { Upload, FileText, X } from "lucide-react";
import { uploadRequiredFile } from "../../../../services/fileServices/FileService";
import { toast } from "react-toastify";
import { TaskRequiresAttachment } from "../../../../interfaces/task/TaskFiles/TaskRequiresAttachment";
import Loader from "../../loader/Loader";
import { MAX_FILES_UPLOAD } from "../../../../Data/SD";
import "../../drive/Drive/personalDrive/Modals/FileUploadModal.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
interface UploadFileModalProp {
  showUploadFileModal: boolean;
  handleCloseUploadFileModal: () => void;
  refreshAttachments: () => void;
  authToken: string;
  taskId: string;
  attachment?: TaskRequiresAttachment | null;
}

interface FileWithTime {
  file: File | null;
  durationInMinutes: number | "";
}

const UploadFileModal = ({
  showUploadFileModal,
  handleCloseUploadFileModal,
  refreshAttachments,
  taskId,
  authToken,
  attachment,
}: UploadFileModalProp) => {
  const [validated, setValidated] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileWithTime[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [uploadResults, setUploadResults] = useState<{
    success: string[];
    failed: string[];
  }>({ success: [], failed: [] });
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  // Validate and add files
  const validateAndAddFiles = (files: File[]) => {
    let filesToAdd = Array.from(files);

    // Check max files limit
    const remainingSlots = MAX_FILES_UPLOAD - selectedFiles.length;
    if (filesToAdd.length > remainingSlots) {
      toast.warning(
        `You can only upload ${MAX_FILES_UPLOAD} files total. Adding first ${remainingSlots} file(s).`
      );
      filesToAdd = filesToAdd.slice(0, remainingSlots);
    }

    const validFiles: FileWithTime[] = [];

    filesToAdd.forEach((file) => {
      // Check if file already exists
      const isDuplicate = selectedFiles.some(
        (f) => f.file && f.file.name === file.name && f.file.size === file.size
      );
      if (isDuplicate) {
        toast.info(`"${file.name}" is already selected.`);
        return;
      }

      validFiles.push({ file, durationInMinutes: "" });
    });

    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles]);
    }
  };

  // Handle file input change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      validateAndAddFiles(files);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle drag events
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      validateAndAddFiles(files);
    }
  };

  // Remove individual file
  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Browse files button click
  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  // Handle duration change for a specific file
  const handleDurationChange = (index: number, value: string) => {
    setSelectedFiles((inputs) =>
      inputs.map((input, i) =>
        i === index
          ? {
              ...input,
              durationInMinutes: value === "" ? "" : parseInt(value, 10),
            }
          : input
      )
    );
  };

  // Upload all files
  const handleUploadAll = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setValidated(true);

    // Validate all fields
    const allValid = selectedFiles.every(
      (input) => input.file && input.durationInMinutes
    );
    if (!allValid) {
      toast.error("Please fill all required fields for each file.");
      return;
    }

    setLoading(true);
    setUploadResults({ success: [], failed: [] });

    const uploadPromises = selectedFiles.map((input) => {
      const { file, durationInMinutes } = input;
      if (!file || !durationInMinutes)
        return Promise.resolve({
          status: "rejected",
          fileName: file?.name || "Unknown",
        });

      const fileSize = file.size;
      const mimeType = file.type || "application/octet-stream";
      return uploadRequiredFile(
        file,
        taskId,
        durationInMinutes.toString(),
        mimeType,
        fileSize,
        authToken,
        attachment ? attachment.id : null,
        attachment ? attachment.templateFile.fileName : file.name
      )
        .then((success) => ({
          status: success ? "fulfilled" : "rejected",
          fileName: file.name,
        }))
        .catch(() => ({
          status: "rejected",
          fileName: file.name,
        }));
    });

    try {
      const results = await Promise.all(uploadPromises);
      const success: string[] = [];
      const failed: string[] = [];
      results.forEach((r) => {
        if (r.status === "fulfilled") success.push(r.fileName);
        else failed.push(r.fileName);
      });
      setUploadResults({ success, failed });

      if (success.length) {
        toast.success(`${success.length} file(s) uploaded successfully`);
        handleModalClose();
      }
      if (failed.length)
        toast.error(
          failed
            .map((f) =>
              f.includes("FILE_TOO_LARGE")
                ? `This file exceeds the maximum upload size of 30 MB. Please choose a smaller file: ${f}`
                : `${f} failed to upload This file exceeds the maximum upload size of 30 MB`
            )
            .join("\n")
        );

      if (success.length) refreshAttachments();
    } finally {
      setLoading(false);
    }
  };

  // Reset state on close
  const handleModalClose = () => {
    setSelectedFiles([]);
    setUploadResults({ success: [], failed: [] });
    setValidated(false);
    setIsDragging(false);
    handleCloseUploadFileModal();
  };

  return (
    <>
      {loading && <Loader />}
      <Modal
        show={showUploadFileModal}
        onHide={handleModalClose}
        className="custom_popup"
        centered
        backdrop="static"
        keyboard={false}
      >
        <Form noValidate validated={validated} onSubmit={handleUploadAll}>
          <div className="modal-activity-reject-header">
            <h2 className="modal-activity-reject-title">Upload Attachments</h2>
            <button
              className="modal-activity-reject-close items-center"
              onClick={handleModalClose}
              type="button"
            >
              <FontAwesomeIcon icon={faTimes} className="w-[1rem] h-[1rem]" />
            </button>
          </div>
          <Modal.Body className="">
            <div className="upload-area-wrapper">
              <div
                className={`upload-area p-[2.12rem] ${
                  isDragging ? "drag-over" : ""
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleBrowseClick}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple={!attachment}
                  onChange={handleFileChange}
                  accept=".jpg,.jpeg,.png,.gif,.pdf"
                  style={{ display: "none" }}
                />

                <div className="upload-icon w-[4rem] h-[4rem] mb-[1rem]">
                  <Upload className="w-[2rem] h-[2rem]" />
                </div>

                <div className="upload-text">
                  <p>
                    <span className="click-text !font-Arial !text-[#7E57C2]">
                      Click to upload
                    </span>{" "}
                    or drag and drop
                  </p>
                </div>

                <div className="upload-size-info">Max Size: 30MB</div>

                <button
                  type="button"
                  className="browse-btn !py-[0.75rem] text-[#0A0A0A] !bg-[#F9F6FF] !text-[0.875rem] !px-[1.62rem]"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBrowseClick();
                  }}
                >
                  Browse Files
                </button>
              </div>
            </div>

            {/* Selected Files with Time Input */}
            {selectedFiles.length > 0 && (
              <div className="selected-files-section">
                <div className="selected-files-title">
                  Selected Files ({selectedFiles.length})
                </div>
                {selectedFiles.map((fileInput, index) => (
                  <div
                    key={index}
                    className="file-item !pt-[0.5rem] !pb-[0.81rem] items-start mt- !pl-[0.75rem] !important mb-[1.5rem] !important"
                  >
                    <div className="file-icon !bg-[#FFE2E2] !rounded-[0.520rem]">
                      {/* <FileText /> */}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="1.25rem"
                        height="1.25rem"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M3.5 4C3.5 3.46957 3.71071 2.96086 4.08579 2.58579C4.46086 2.21071 4.96957 2 5.5 2H10.086C10.6164 2.00011 11.125 2.2109 11.5 2.586L14.914 6C15.2891 6.37499 15.4999 6.88361 15.5 7.414V16C15.5 16.5304 15.2893 17.0391 14.9142 17.4142C14.5391 17.7893 14.0304 18 13.5 18H5.5C4.96957 18 4.46086 17.7893 4.08579 17.4142C3.71071 17.0391 3.5 16.5304 3.5 16V4Z"
                          fill="#E7000B"
                        />
                      </svg>
                    </div>
                    <div className="file-info">
                      <p className="file-name !text-[#314158] !px-0">
                        {fileInput.file?.name}
                      </p>
                      <p className="file-size">
                        {fileInput.file
                          ? formatFileSize(fileInput.file.size)
                          : ""}
                      </p>
                      <Form.Group
                        controlId={`duration-${index}`}
                        className="mt-[0.62rem] "
                      >
                        <Form.Control
                          type="number"
                          placeholder="Time Spent (min)"
                          className="!bg-[#E2E2E2] !text-black !h-[1.75rem] !placeholder:text-black !rounded-[0.375rem]"
                          required
                          value={fileInput.durationInMinutes}
                          onChange={(e) =>
                            handleDurationChange(index, e.target.value)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "e" || e.key === "E") {
                              e.preventDefault();
                            }
                          }}
                          min={1}
                          size="sm"
                        />
                        <Form.Control.Feedback type="invalid">
                          Please enter time spent
                        </Form.Control.Feedback>
                      </Form.Group>
                    </div>
                    <button
                      type="button"
                      className="file-remove"
                      onClick={() => handleRemoveFile(index)}
                      aria-label="Remove file"
                    >
                      <X />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload Results */}
            {(uploadResults.success.length > 0 ||
              uploadResults.failed.length > 0) && (
              <div className="upload-results">
                {uploadResults.success.length > 0 && (
                  <>
                    <h6 className="text-success">Uploaded:</h6>
                    <ul>
                      {uploadResults.success.map((name, i) => (
                        <li key={i}>{name}</li>
                      ))}
                    </ul>
                  </>
                )}
                {uploadResults.failed.length > 0 && (
                  <>
                    <h6 className="text-danger">Failed:</h6>
                    <ul>
                      {uploadResults.failed.map((name, i) => (
                        <li key={i}>{name}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="!mt-[0.81rem] !mb-[1.69rem] !gap-[0.75rem] !pt-[1rem] mx-[1.56rem]">
            <Button
              className="btn btn-secondary cancel-btn"
              onClick={handleModalClose}
            >
              Cancel
            </Button>
            <Button
              className="btn btn-primary submit-btn"
              type="submit"
              disabled={!selectedFiles.length || loading}
            >
              {loading
                ? "Uploading..."
                : `Upload${
                    selectedFiles.length > 0 ? ` (${selectedFiles.length})` : ""
                  }`}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default UploadFileModal;
